import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { CalendarIcon, GridIcon, ScaleIcon, SparkleIcon } from '../components/icons';
import EntryForm from '../components/finances/EntryForm';
import EntryTable from '../components/finances/EntryTable';
import MonthlySummary from '../components/finances/MonthlySummary';
import TaxEstimate from '../components/finances/TaxEstimate';
import IncomeStatement from '../components/finances/IncomeStatement';
import BalanceSheet from '../components/finances/BalanceSheet';
import PeriodPicker from '../components/finances/PeriodPicker';
import { useAuth } from '../context/AuthContext';
import {
  addBalanceSheetItem,
  addExpense,
  addIncome,
  CIT_RATE,
  deleteBalanceSheetItem,
  deleteExpense,
  deleteIncome,
  formatJMD,
  GCT_THRESHOLD_JMD,
  getYears,
  listBalanceSheetItems,
  listExpenses,
  listIncome,
  rollingTwelveMonthTotal,
  sumJmd,
  updateExpense,
  updateIncome,
} from '../lib/finances';
import {
  balanceSheetCsv,
  buildBalanceSheet,
  buildIncomeStatement,
  expensesCsv,
  incomeCsv,
  incomeStatementCsv,
  monthlySummaryCsv,
  retainedEarningsThrough,
  taxEstimateCsv,
} from '../lib/financeReports';
import { downloadCsv } from '../lib/csv';
import { effectiveRange, filterByPeriod, resolvePeriod, toDateInputValue } from '../lib/period';
import { addVendor, listVendors } from '../lib/vendors';
import './Finances.css';
import { useConfirm } from '../components/ConfirmDialog';

// Short labels: six full titles ("P&L Income Statement") won't fit a tab
// strip without wrapping. The page heading carries the long form.
const TABS = [
  { id: 'Expenses', label: 'Expenses', Icon: GridIcon },
  { id: 'Income', label: 'Income', Icon: GridIcon },
  { id: 'Monthly Summary', label: 'Monthly', Icon: CalendarIcon },
  { id: 'Tax Estimate', label: 'Tax', Icon: ScaleIcon },
  { id: 'P&L Income Statement', label: 'P&L', Icon: ScaleIcon },
  { id: 'Balance Sheet', label: 'Balance Sheet', Icon: ScaleIcon },
];

const LEDGER_TABS = ['Expenses', 'Income'];

export default function Finances() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [tab, setTab] = useState(TABS[0].id);
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [balanceItems, setBalanceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [balanceError, setBalanceError] = useState(null);
  const [periodValue, setPeriodValue] = useState(String(new Date().getFullYear()));
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingIncome, setEditingIncome] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // The balance sheet is fetched separately and its failure is contained:
      // it's the only table the other five tabs don't need, so a problem
      // there (or the migration simply not being run yet) must not blank out
      // expenses, income, and every statement derived from them.
      const balancePromise = listBalanceSheetItems().then(
        (rows) => {
          setBalanceItems(rows);
          setBalanceError(null);
        },
        (err) => {
          setBalanceItems([]);
          setBalanceError(err.message);
        }
      );

      const [expenseRows, incomeRows, vendorRows] = await Promise.all([
        listExpenses(),
        listIncome(),
        listVendors(),
      ]);
      await balancePromise;

      setExpenses(expenseRows);
      setIncome(incomeRows);
      setVendors(vendorRows);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const years = getYears([...expenses, ...income]);
  const period = resolvePeriod(periodValue, { customStart, customEnd });
  // The balance sheet and the tax year both need one concrete date, even
  // when the period itself is open-ended ("All time").
  const asOf = period.end || effectiveRange(period, [...expenses, ...income]).end;
  const taxYear = asOf.getFullYear();
  const asOfLabel = toDateInputValue(asOf);
  const periodExpenses = filterByPeriod(expenses, period);
  const periodIncome = filterByPeriod(income, period);
  const taxYearPeriod = { start: new Date(taxYear, 0, 1), end: new Date(taxYear, 11, 31) };
  const taxYearExpenses = filterByPeriod(expenses, taxYearPeriod);
  const taxYearIncome = filterByPeriod(income, taxYearPeriod);

  const handleSubmitExpense = async ({ id, ...entry }) => {
    if (id) {
      await updateExpense(id, entry);
      setEditingExpense(null);
    } else {
      await addExpense({ ...entry, created_by: user.id });
    }
    setEntryFormOpen(false);
    await loadAll();
  };

  const startEditExpense = (entry) => {
    setEditingExpense(entry);
    setEntryFormOpen(true);
  };

  const startEditIncome = (entry) => {
    setEditingIncome(entry);
    setEntryFormOpen(true);
  };

  const handleDeleteExpense = async (id) => {
    if (!(await confirm({
      title: 'Delete this expense?',
      body: "It will disappear from the P&L, the tax estimate and every report for that period.",
    }))) return;

    await deleteExpense(id);
    if (editingExpense?.id === id) setEditingExpense(null);
    await loadAll();
  };

  const handleSubmitIncome = async ({ id, ...entry }) => {
    if (id) {
      await updateIncome(id, entry);
      setEditingIncome(null);
    } else {
      await addIncome({ ...entry, created_by: user.id });
    }
    setEntryFormOpen(false);
    await loadAll();
  };

  const handleDeleteIncome = async (id) => {
    if (!(await confirm({
      title: 'Delete this income entry?',
      body: "If an invoice created it, that invoice will show as paid with nothing in the books behind it.",
    }))) return;

    await deleteIncome(id);
    if (editingIncome?.id === id) setEditingIncome(null);
    await loadAll();
  };

  const handleCreateVendor = async (name) => {
    const vendor = await addVendor(name);
    setVendors((prev) => [...prev, vendor].sort((a, b) => a.name.localeCompare(b.name)));
    return vendor;
  };

  const handleAddBalanceItem = async (item) => {
    await addBalanceSheetItem({ ...item, created_by: user.id });
    await loadAll();
  };

  const handleDeleteBalanceItem = async (id) => {
    if (!(await confirm({
      title: 'Delete this balance sheet line?',
      body: "Snapshots are history — correcting a figure is usually better done by adding a newer one.",
    }))) return;

    await deleteBalanceSheetItem(id);
    await loadAll();
  };

  // Each tab exports exactly what it shows, for the selected period. Row
  // building lives in lib/financeReports.js so the CSV and the on-screen
  // table are always derived from the same numbers.
  const buildExport = () => {
    switch (tab) {
      case 'Expenses':
        return { name: 'expenses', rows: expensesCsv(periodExpenses) };
      case 'Income':
        return { name: 'income', rows: incomeCsv(periodIncome) };
      case 'Monthly Summary':
        return {
          name: 'monthly-summary',
          rows: monthlySummaryCsv(income, expenses, period, period.label),
        };
      case 'Tax Estimate': {
        // Corporate income tax is assessed on a full year, so this tab stays
        // keyed to the period's end year rather than an arbitrary span.
        const incomeTotal = sumJmd(taxYearIncome);
        const expenseTotal = sumJmd(taxYearExpenses);
        const netChargeable = Math.max(0, incomeTotal - expenseTotal);
        return {
          name: 'tax-estimate',
          rows: taxEstimateCsv({
            year: taxYear,
            incomeTotal,
            expenseTotal,
            netChargeable,
            citRate: CIT_RATE,
            citDue: netChargeable * CIT_RATE,
            rollingRevenue: rollingTwelveMonthTotal(income),
            gctThreshold: GCT_THRESHOLD_JMD,
          }),
        };
      }
      case 'P&L Income Statement':
        return {
          name: 'profit-and-loss',
          rows: incomeStatementCsv(buildIncomeStatement(income, expenses, period), period.label),
        };
      case 'Balance Sheet':
        return {
          name: 'balance-sheet',
          rows: balanceSheetCsv(
            buildBalanceSheet(balanceItems, asOf, retainedEarningsThrough(income, expenses, asOf)),
            asOfLabel
          ),
        };
      default:
        return null;
    }
  };

  const handleExport = () => {
    const report = buildExport();
    if (!report) return;
    const slug = period.label.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
    downloadCsv(`skin-fairy-ja-${report.name}-${slug}.csv`, report.rows);
  };

  const periodIncomeTotal = sumJmd(periodIncome);
  const periodExpenseTotal = sumJmd(periodExpenses);
  const periodNet = periodIncomeTotal - periodExpenseTotal;
  const isLedger = LEDGER_TABS.includes(tab);

  return (
    <>
      <PageHeader
        title={
          <>
            Accounting
            <SparkleIcon className="page-title-sparkle" />
          </>
        }
        subtitle="Every dollar in and out, with USD converted to JMD, a running tax estimate, and full statements."
        actions={
          isLedger && !entryFormOpen ? (
            <button
              className="primary-action"
              onClick={() => {
                setEditingExpense(null);
                setEditingIncome(null);
                setEntryFormOpen(true);
              }}
            >
              + Add {tab === 'Expenses' ? 'expense' : 'income'}
            </button>
          ) : null
        }
      />

      <nav className="view-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={'view-tab' + (tab === id ? ' active' : '')}
            onClick={() => setTab(id)}
          >
            <Icon />
            {label}
          </button>
        ))}
      </nav>

      <div className="finances-toolbar">
        <PeriodPicker
          value={periodValue}
          onChange={setPeriodValue}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStart={setCustomStart}
          onCustomEnd={setCustomEnd}
          years={years}
          label={period.label}
        />
        <button
          className="ghost-button finances-export"
          onClick={handleExport}
          disabled={loading}
          title={`Export the ${tab} tab for ${period.label} as CSV`}
        >
          ↓ Export CSV
        </button>
      </div>

      {/* The period's headline figures, above whichever tab is open — the
          numbers you'd otherwise go to Monthly Summary to read. */}
      {!loading && (
        <div className="finances-summary">
          <div className="finances-stat">
            <span className="finances-stat-label">Income</span>
            <span className="finances-stat-value">{formatJMD(periodIncomeTotal)}</span>
          </div>
          <div className="finances-stat">
            <span className="finances-stat-label">Expenses</span>
            <span className="finances-stat-value">{formatJMD(periodExpenseTotal)}</span>
          </div>
          <div className="finances-stat">
            <span className="finances-stat-label">Net</span>
            <span className={'finances-stat-value' + (periodNet < 0 ? ' negative' : ' positive')}>
              {formatJMD(periodNet)}
            </span>
          </div>
          <span className="finances-summary-period">{period.label}</span>
        </div>
      )}

      {loadError && <p className="finances-error">Couldn't load data: {loadError}</p>}

      {loading ? (
        <p className="finances-loading">Loading…</p>
      ) : (
        <>
          {tab === 'Expenses' && (
            <>
              {(entryFormOpen || editingExpense) && (
                <EntryForm
                  type="expense"
                  editingEntry={editingExpense}
                  vendors={vendors}
                  onCreateVendor={handleCreateVendor}
                  onSubmit={handleSubmitExpense}
                  onCancelEdit={() => {
                    setEditingExpense(null);
                    setEntryFormOpen(false);
                  }}
                />
              )}
              <section className="panel">
                <EntryTable
                  type="expense"
                  entries={periodExpenses}
                  onEdit={startEditExpense}
                  onDelete={handleDeleteExpense}
                />
              </section>
            </>
          )}

          {tab === 'Income' && (
            <>
              {(entryFormOpen || editingIncome) && (
                <EntryForm
                  type="income"
                  editingEntry={editingIncome}
                  onSubmit={handleSubmitIncome}
                  onCancelEdit={() => {
                    setEditingIncome(null);
                    setEntryFormOpen(false);
                  }}
                />
              )}
              <section className="panel">
                <EntryTable
                  type="income"
                  entries={periodIncome}
                  onEdit={startEditIncome}
                  onDelete={handleDeleteIncome}
                />
              </section>
            </>
          )}

          {tab === 'Monthly Summary' && (
            <section className="panel">
              <MonthlySummary
                expenses={expenses}
                income={income}
                period={period}
                periodLabel={period.label}
              />
            </section>
          )}

          {tab === 'Tax Estimate' && (
            <section className="panel">
              <TaxEstimate expenses={expenses} income={income} year={taxYear} />
            </section>
          )}

          {tab === 'P&L Income Statement' && (
            <section className="panel">
              <IncomeStatement
                income={income}
                expenses={expenses}
                period={period}
                periodLabel={period.label}
              />
            </section>
          )}

          {tab === 'Balance Sheet' && balanceError && (
            <p className="finances-error">Couldn't load balance sheet items: {balanceError}</p>
          )}

          {tab === 'Balance Sheet' && (
            <section className="panel">
              <BalanceSheet
                items={balanceItems}
                income={income}
                expenses={expenses}
                asOf={asOf}
                asOfLabel={asOfLabel}
                onAdd={handleAddBalanceItem}
                onDelete={handleDeleteBalanceItem}
              />
            </section>
          )}
        </>
      )}
    </>
  );
}
